/*
 * Purpose: Block palette SSOT — templates for manual map placement
 * Last modified: 2026-06-27 | Agent
 * Completeness: 95
 */
import { BLOCK_REGISTRY } from '@/services/blocks/registry';
import { getBlockTypeLabel } from '@/lib/blockTypes';
import type { Node, Workflow } from '@/stores/workflowStore';
import { initializePlacementContext } from '@/lib/blockContext';
import { getBrandedBlockDef } from '@/lib/brandedBlocks';
import { missionStartBlocks } from '@/lib/missionRules';

export type BlockTemplate = {
  type: Node['type'];
  subtype: Node['subtype'];
  label: string;
};

export const BLOCK_PALETTE: BlockTemplate[] = [
  ...BLOCK_REGISTRY.map((b) => ({
    type: b.category,
    subtype: b.subtype,
    label: b.label,
  })),
  { type: 'trigger', subtype: 'api', label: getBlockTypeLabel('trigger', 'api') },
];

export function buildNodeFromTemplate(
  template: BlockTemplate,
  workflow: Workflow,
  x: number,
  y: number
): Node {
  let defaultConfig: Record<string, unknown> = initializePlacementContext(
    workflow.id,
    template.type,
    template.subtype,
    template.label
  );

  if (template.type === 'start' && template.subtype === 'mission') {
    defaultConfig = { ...defaultConfig, name: 'Start' };
  } else if (template.type === 'goal' && template.subtype === 'outcome') {
    defaultConfig = { ...defaultConfig, outcome: '', name: 'Identified outcome' };
  } else if (template.type === 'agent' && template.subtype === 'assistant') {
    defaultConfig = { ...defaultConfig, agentBrief: '', autonomy: 'supervised' };
  } else if (template.type === 'drone' && template.subtype === 'remote') {
    defaultConfig = { ...defaultConfig, droneTask: '', remoteTarget: '' };
  } else if (template.type === 'branded') {
    const branded = getBrandedBlockDef(template.subtype);
    defaultConfig = {
      ...defaultConfig,
      brand: branded?.brand,
      ...(branded?.defaultConfig ?? {}),
    };
  }

  return {
    id: Math.random().toString(36).slice(2, 11),
    type: template.type,
    subtype: template.subtype,
    config: defaultConfig,
    x,
    y,
  };
}

export function canPlaceStartBlock(workflow: Workflow): boolean {
  return missionStartBlocks(workflow).length === 0;
}

export function paletteByCategory(category: Node['type']): BlockTemplate[] {
  return BLOCK_PALETTE.filter((t) => t.type === category);
}
